import { useCallback, useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";

/**
 * useDashboardCache — offline-safe data hook for the dashboard.
 *
 * • Reads the last cached response from localStorage on mount so the dashboard
 *   never renders empty on a cold offline start.
 * • Fires the network fetch in parallel; overwrites cache on success.
 * • Exposes { data, loading, error, lastSyncedAt, reload }.
 */
export function useDashboardCache(cacheKey, url, { params, enabled = true } = {}) {
  const [data, setData] = useState(() => {
    try {
      const raw = localStorage.getItem(cacheKey);
      if (!raw) return null;
      const parsed = JSON.parse(raw);
      return parsed.data ?? null;
    } catch {
      return null;
    }
  });
  const [lastSyncedAt, setLastSyncedAt] = useState(() => {
    try {
      const raw = localStorage.getItem(cacheKey);
      if (!raw) return null;
      return JSON.parse(raw).at || null;
    } catch {
      return null;
    }
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const mounted = useRef(true);

  const fetchOnce = useCallback(async () => {
    if (!enabled) return;
    setLoading(true);
    setError(null);
    try {
      const r = await api.get(url, { params });
      if (!mounted.current) return;
      const at = new Date().toISOString();
      setData(r.data);
      setLastSyncedAt(at);
      try {
        localStorage.setItem(cacheKey, JSON.stringify({ data: r.data, at }));
      } catch (_) { /* localStorage full or unavailable — skip caching */ }
    } catch (e) {
      if (!mounted.current) return;
      setError(e);
    } finally {
      if (mounted.current) setLoading(false);
    }
  }, [cacheKey, url, JSON.stringify(params || {}), enabled]);  

  useEffect(() => {
    mounted.current = true;
    fetchOnce();
    return () => {
      mounted.current = false;
    };
  }, [fetchOnce]);

  return { data, loading, error, lastSyncedAt, reload: fetchOnce };
}
